import React, { useCallback } from 'react';
import { hooks, ObservableEvent } from '@lifesize/clients.sdk';
import { injectIntl } from 'react-intl';
import { connect, useDispatch } from 'react-redux';
import Footer from 'components/InCall/CallFooter';
import actions from 'actions/InCall/InCallVideo';
import { setTooltipNotification } from 'actions/Notification';
import { tooltipNotifications, tooltipNotificationTypes } from 'constants/Notification';
import { callActions } from '@lifesize/nucleus';
import _get from 'lodash/get';
import useIsMinimized from 'hooks/useIsMinimized';

const messages = {
  otherHandRaised: { id: 'notification.tooltip.otherHandRaised', defaultMessage: '{name} has raised their hand' },
  ownHandLoweredByModerator: {
    id: 'notification.tooltip.ownHandLoweredByModerator',
    defaultMessage: 'A moderator has lowered your hand'
  },
  ownHandLoweredBySelf: {
    id: 'notification.tooltip.ownHandLoweredBySelf',
    defaultMessage: 'Your hand has been lowered'
  },
  ownHandLoweredBySelfFailure: {
    id: 'notification.tooltip.ownHandLoweredBySelfFailure',
    defaultMessage: 'An error occurred while lowering your hand',
    messageType: tooltipNotificationTypes.error
  },
  ownHandRaisedBySelf: { id: 'notification.tooltip.ownHandRaisedBySelf', defaultMessage: 'Your hand has been raised' },
  ownHandRaisedBySelfFailure: {
    id: 'notification.tooltip.ownHandRaisedBySelfFailure',
    defaultMessage: 'An error occurred while raising your hand',
    messageType: tooltipNotificationTypes.error
  }
};
/**
 * This container will have management for call controls.
 * Mute, Hangup, local pip display, etc...
 */

const mapActionCreators = (dispatch) => ({
  onDTMFClicked: (number) => dispatch(callActions.sendDtmf(number)),
  toggleDialPad: () => {
    dispatch(actions.toggleDialPad());
  }
});

const mapStateToProps = (state, ownProps) => {
  return {
    ...ownProps,
    showCameraControls: _get(state, 'app.inCallVideo.showCameraControls'),
    showDialPad: _get(state, 'app.inCallVideo.showDialPad'),
    startTime: ownProps.call.startTime
  };
};

const ConnectedComponent = injectIntl(connect(mapStateToProps, mapActionCreators)(Footer));

const FunctionalWrapper = (props) => {
  const selfParticipant = hooks.useCurrentUserParticipant() || {};
  const { uuid: currentUserUuid } = selfParticipant;
  const call = hooks.useCallState();
  const dispatch = useDispatch();
  const isMinimized = useIsMinimized();

  const onHandRaised = useCallback(
    (e) => {
      if (currentUserUuid !== _get(e, 'participant.uuid')) {
        dispatch(
          setTooltipNotification(tooltipNotifications.inCallDrawerBtnRaiseHand, {
            ...messages.otherHandRaised,
            values: { name: _get(e, 'participant.name') }
          })
        );
      }
    },
    [dispatch, currentUserUuid]
  );

  const onAllHandsLowered = useCallback(
    (e) => {
      const initiatedByUuid = _get(e, 'initiatedBy.uuid');
      if (currentUserUuid !== initiatedByUuid) {
        dispatch(
          setTooltipNotification(tooltipNotifications.inCallActionBtnRaiseHand, {
            ...messages.ownHandLoweredByModerator,
            values: { name: _get(e, 'initiatedBy.displayName') }
          })
        );
      }
    },
    [dispatch, currentUserUuid]
  );

  const onHandLowered = useCallback(
    (e) => {
      const initiatedByUuid = _get(e, 'initiatedBy.uuid');
      if (currentUserUuid === _get(e, 'participant.uuid') && currentUserUuid !== initiatedByUuid) {
        dispatch(
          setTooltipNotification(tooltipNotifications.inCallActionBtnRaiseHand, {
            ...messages.ownHandLoweredByModerator,
            values: { name: _get(e, 'initiatedBy.displayName') }
          })
        );
      }
    },
    [dispatch, currentUserUuid]
  );

  hooks.useObservableEvent(ObservableEvent.HandRaised, onHandRaised);
  hooks.useObservableEvent(ObservableEvent.AllHandsLowered, onAllHandsLowered);
  hooks.useObservableEvent(ObservableEvent.HandLowered, onHandLowered);

  return <ConnectedComponent {...props} call={call} selfParticipant={selfParticipant} isMinimized={isMinimized} />;
};

export default FunctionalWrapper;
